import { ApiService } from "@/services/api/ApiService";
import {
  AddPhoneNumberParams,
  BillingAllData,
  BillingPortalParams,
  BillingSyncResult,
  CallPurchase,
  CheckoutSessionParams,
  ISubscriptionService,
  PhoneBillingData,
  PhoneSubscription,
  StripeCustomerInspection,
  StripeMode,
  SubscriptionData,
  UnlinkedStripeSubscription,
} from "./ISubscriptionService";

const toBillingSyncResult = (data: Partial<BillingSyncResult> | null): BillingSyncResult => ({
  success: true,
  message: data?.message || "Billing data synced successfully",
  synced: data?.synced ?? 0,
  updated: data?.updated ?? 0,
  skipped: data?.skipped ?? [],
  adjustments: data?.adjustments ?? [],
});

export class SupabaseSubscriptionService implements ISubscriptionService {
  async getSubscription(
    userId?: string,
    subscriptionId?: string | number,
  ): Promise<SubscriptionData | null> {
    const result = await ApiService.invoke<{ subscription: SubscriptionData | null }>({
      resource: "billing",
      action: "get",
      ...(subscriptionId && { subscriptionId }),
    });
    return result?.subscription ?? null;
  }

  async createCheckoutSession(params: CheckoutSessionParams): Promise<string> {
    const checkoutSession = await ApiService.callEdge<{ sessionId: string }>(
      "stripe-handler",
      {
        action: "checkout",
        email: params.email,
        userId: params.userId,
        customerId: params.customerId,
        quantity: params.quantity,
        priceId: params.priceId,
        // mode: "test",
      },
      "public",
    );

    if (!checkoutSession?.sessionId) {
      throw new Error("Failed to create checkout session");
    }

    return checkoutSession.sessionId;
  }

  async updateSubscriptionQuantity(
    newQuantity: number,
    subscriptionId?: string | number,
  ): Promise<{ success: boolean; message?: string }> {
    const data = await ApiService.callEdge<{ success: boolean; message?: string }>(
      "stripe-handler",
      {
        action: "updateSubscription",
        newQuantity,
        ...(subscriptionId && { subscriptionId }),
        // mode: "test",
      },
    );

    if (!data.success) {
      throw new Error(data.message || "Failed to update subscription");
    }

    return data;
  }

  async getBillingPortalUrl(params: BillingPortalParams): Promise<string> {
    const data = await ApiService.callEdge<{ url: string }>("stripe-handler", {
      action: "billingPortal",
      // mode: "test",
      returnUrl: params.returnUrl,
    });

    if (!data?.url) {
      throw new Error("No billing portal URL received");
    }

    return data.url;
  }

  async syncBillingData(): Promise<BillingSyncResult> {
    const data = await ApiService.callEdge<Partial<BillingSyncResult>>("stripe-handler", {
      action: "syncBilling",
    });

    return toBillingSyncResult(data);
  }

  async linkStripeCustomer(userId: string, customerId: string): Promise<BillingSyncResult> {
    const data = await ApiService.callEdge<Partial<BillingSyncResult>>("stripe-handler", {
      action: "linkStripeCustomer",
      userId,
      customerId,
    });

    return toBillingSyncResult(data);
  }

  async listUnlinkedSubscriptions(): Promise<UnlinkedStripeSubscription[]> {
    const data = await ApiService.callEdge<{ subscriptions?: UnlinkedStripeSubscription[] }>("stripe-handler", {
      action: "listUnlinkedSubscriptions",
    });

    return data?.subscriptions ?? [];
  }

  async inspectStripeCustomer(customerId: string, mode: StripeMode): Promise<StripeCustomerInspection> {
    return ApiService.callEdge<StripeCustomerInspection>("stripe-handler", {
      action: "inspectStripeCustomer",
      customerId,
      ...(mode === "test" && { mode: "test" }),
    });
  }

  isSubscriptionActive(subscription: SubscriptionData | null): boolean {
    if (!subscription) return false;
    if (subscription.status !== "active" && subscription.status !== "trialing") return false;

    // If ends_at exists and is in the past, subscription has expired
    if (subscription.ends_at && new Date(subscription.ends_at) <= new Date()) {
      return false;
    }

    return true;
  }

  async purchaseAgentCalls(
    agentId: string,
    quantity: number,
  ): Promise<{ success: boolean; message: string; newCallLimit?: number }> {
    const data = await ApiService.callEdge<{
      success: boolean;
      message: string;
      newCallLimit?: number;
    }>("stripe-handler", {
      action: "purchaseAgentCalls",
      agentId,
      quantity,
      //mode: "test",
    });

    if (!data.success) {
      throw new Error(data.message || "Failed to purchase calls");
    }

    return data;
  }

  async cancelAgentCalls(
    agentId: string,
    quantity?: number,
  ): Promise<{ success: boolean; message: string; newCallLimit?: number }> {
    const data = await ApiService.callEdge<{
      success: boolean;
      message: string;
      newCallLimit?: number;
    }>("stripe-handler", {
      action: "cancelAgentCalls",
      agentId,
      ...(quantity !== undefined && { quantity }),
      //mode: "test",
    });

    if (!data.success) {
      throw new Error(data.message || "Failed to cancel calls");
    }

    return data;
  }

  async addPhoneNumber(
    params: AddPhoneNumberParams,
  ): Promise<{ success: boolean; message: string; subscriptionItemId?: string }> {
    const data = await ApiService.callEdge<{
      success: boolean;
      message: string;
      subscriptionItemId?: string;
    }>("stripe-handler", {
      action: "addPhoneNumber",
      //mode: "test",
      ...params,
    });

    if (!data.success) {
      throw new Error(data.message || "Failed to add phone number");
    }

    return data;
  }

  async removePhoneNumber(phoneNumber: string): Promise<{ success: boolean; message: string }> {
    const data = await ApiService.callEdge<{ success: boolean; message: string }>(
      "stripe-handler",
      {
        action: "removePhoneNumber",
        phoneNumber,
        //mode: "test",
      },
    );

    if (!data.success) {
      throw new Error(data.message || "Failed to remove phone number");
    }

    return data;
  }

  async getPhoneSubscriptions(): Promise<PhoneSubscription[]> {
    const result = await ApiService.invoke<{ phoneSubscriptions: PhoneSubscription[] }>({
      resource: "billing",
      action: "phones",
    });
    return result?.phoneSubscriptions ?? [];
  }

  async getPhoneBilling(): Promise<PhoneBillingData> {
    const result = await ApiService.invoke<Partial<PhoneBillingData>>({
      resource: "billing",
      action: "phones",
    });
    return { phoneSubscriptions: result?.phoneSubscriptions ?? [], numbers: result?.numbers ?? [] };
  }

  async getCallPurchases(): Promise<CallPurchase[]> {
    const result = await ApiService.invoke<{ callPurchases: CallPurchase[] }>({
      resource: "billing",
      action: "calls",
    });
    return result?.callPurchases ?? [];
  }

  async getAll(): Promise<BillingAllData> {
    const result = await ApiService.invoke<BillingAllData>({
      resource: "billing",
      action: "all",
    });
    return result || { subscriptions: [], callPurchases: [], phoneSubscriptions: [] };
  }
}
